import notification from 'antd/lib/notification';
import type { ListResponse } from './api';
import { setQueryParams } from './helpers';

export type ListStatePatch<T> = Partial<{
  data: T[];
  page: number;
  totalCount: number;
  loading: boolean;
}>;

export type FetchListWithLifecycleOptions<
  T,
  Params = Record<string, unknown>
> = {
  page: number | string | string[] | undefined;
  pageSize: number;
  params?: Params;
  fetchPage: (page: number, params?: Params) => Promise<ListResponse<T>>;
  setState: (patch: ListStatePatch<T>) => void;
};

export const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

export const normalizeListPage = (
  page: number | string | string[] | undefined
) => {
  const value = Array.isArray(page) ? page[0] : page;
  const numericPage = Number(value);

  return Number.isFinite(numericPage) ? numericPage : 1;
};

export const clampListPage = (
  page: number,
  totalCount: number,
  pageSize: number
) => {
  const normalizedPage = Number.isFinite(Number(page))
    ? Math.max(1, Math.floor(Number(page)))
    : 1;
  const normalizedPageSize = Number(pageSize) > 0 ? Number(pageSize) : 1;
  const normalizedTotal = Number(totalCount) > 0 ? Number(totalCount) : 0;
  const maxPage = Math.max(1, Math.ceil(normalizedTotal / normalizedPageSize));

  return Math.min(normalizedPage, maxPage);
};

export const fetchListWithLifecycle = async <
  T,
  Params = Record<string, unknown>
>({
  page,
  pageSize,
  params,
  fetchPage,
  setState,
}: FetchListWithLifecycleOptions<T, Params>): Promise<
  ListResponse<T> | undefined
> => {
  const requestedPage = normalizeListPage(page);
  setState({ loading: true });

  try {
    let result = await fetchPage(requestedPage, params);
    const actualPage = clampListPage(requestedPage, result.totalCount, pageSize);

    if (actualPage !== requestedPage) {
      result = await fetchPage(actualPage, params);
    }

    setQueryParams({ page: actualPage });
    setState({
      data: result.data,
      totalCount: result.totalCount,
      page: actualPage,
    });
    return result;
  } catch (error) {
    notification.error({
      message: 'Ошибка',
      description: getErrorMessage(error),
    });
    return undefined;
  } finally {
    setState({ loading: false });
  }
};
